import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { HelpCircle, Phone, Plus, Pencil, Trash2, Loader2 } from "lucide-react";

const FaqDialog = ({ trigger, initial, onSaved }) => {
  const isEdit = Boolean(initial?.id);
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setQuestion(initial?.question || ""); setAnswer(initial?.answer || ""); }
  }, [open, initial]);
  const save = async () => {
    if (!question.trim() || !answer.trim()) { toast.error("أدخل السؤال والجواب"); return; }
    setBusy(true);
    try {
      if (isEdit) await api.updateFaq(initial.id, question.trim(), answer.trim());
      else await api.createFaq(question.trim(), answer.trim());
      toast.success(isEdit ? "تم تحديث السؤال" : "تمت إضافة السؤال");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="faq-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "تعديل سؤال" : "إضافة سؤال شائع"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-slate-700">السؤال</label>
            <Input data-testid="faq-question-input" value={question} onChange={(e) => setQuestion(e.target.value)}
              placeholder="مثال: كيف أسجّل تغيير الزيت؟" className="mt-1" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">الجواب</label>
            <Textarea data-testid="faq-answer-input" value={answer} onChange={(e) => setAnswer(e.target.value)}
              placeholder="اكتب الإجابة هنا…" rows={4} className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="faq-save-btn" onClick={save} disabled={busy} className="gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} حفظ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ContactDialog = ({ trigger, initial, onSaved }) => {
  const isEdit = Boolean(initial?.id);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setName(initial?.name || ""); setPhone(initial?.phone || ""); setNote(initial?.note || ""); }
  }, [open, initial]);
  const save = async () => {
    if (!name.trim() || !phone.trim()) { toast.error("أدخل الاسم ورقم الجوال"); return; }
    setBusy(true);
    try {
      if (isEdit) await api.updateContact(initial.id, name.trim(), phone.trim(), note.trim());
      else await api.createContact(name.trim(), phone.trim(), note.trim());
      toast.success(isEdit ? "تم تحديث الرقم" : "تمت إضافة الرقم");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="contact-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "تعديل رقم" : "إضافة رقم مهم"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-slate-700">الاسم / الجهة</label>
            <Input data-testid="contact-name-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="مثال: إدارة العمليات" className="mt-1" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">رقم الجوال</label>
            <Input data-testid="contact-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="05xxxxxxxx" dir="ltr" className="mt-1 font-mono-nums" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">ملاحظة (اختياري)</label>
            <Input data-testid="contact-note-input" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="مثال: للطوارئ فقط" className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="contact-save-btn" onClick={save} disabled={busy} className="gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} حفظ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const FaqContactsPanel = ({ canEdit = false }) => {
  const [faqs, setFaqs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [del, setDel] = useState(null); // {type, id, label}

  const load = useCallback(async () => {
    try {
      const [f, c] = await Promise.all([api.listFaqs(), api.listContacts()]);
      setFaqs(f.faqs || []);
      setContacts(c.contacts || []);
    } catch { toast.error("تعذّر تحميل البيانات"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doDelete = async () => {
    if (!del) return;
    try {
      if (del.type === "faq") await api.deleteFaq(del.id);
      else await api.deleteContact(del.id);
      toast.success("تم الحذف");
      setDel(null);
      load();
    } catch { toast.error("تعذّر الحذف"); }
  };

  return (
    <div className="space-y-6" data-testid="faq-contacts-panel">
      {/* FAQ section */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-bold text-slate-800">
            <HelpCircle className="h-5 w-5 text-blue-600" /> الأسئلة الشائعة
          </div>
          {canEdit && (
            <FaqDialog onSaved={load}
              trigger={<Button data-testid="add-faq-btn" size="sm" className="gap-1.5"><Plus className="h-4 w-4" /> إضافة سؤال</Button>} />
          )}
        </div>
        {faqs.length === 0 ? (
          <p className="text-sm text-slate-400 p-6 text-center">لا توجد أسئلة بعد.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {faqs.map((f) => (
              <div key={f.id} data-testid={`faq-row-${f.id}`} className="p-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 text-sm">{f.question}</div>
                  <div className="text-sm text-slate-500 mt-1 whitespace-pre-wrap leading-relaxed">{f.answer}</div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 shrink-0">
                    <FaqDialog initial={f} onSaved={load}
                      trigger={<Button data-testid={`edit-faq-${f.id}`} variant="ghost" size="icon" className="h-8 w-8 text-slate-500"><Pencil className="h-4 w-4" /></Button>} />
                    <Button data-testid={`del-faq-${f.id}`} variant="ghost" size="icon" className="h-8 w-8 text-rose-500"
                      onClick={() => setDel({ type: "faq", id: f.id, label: f.question })}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Contacts section */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-bold text-slate-800">
            <Phone className="h-5 w-5 text-emerald-600" /> أرقام تهمك
          </div>
          {canEdit && (
            <ContactDialog onSaved={load}
              trigger={<Button data-testid="add-contact-btn" size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"><Plus className="h-4 w-4" /> إضافة رقم</Button>} />
          )}
        </div>
        {contacts.length === 0 ? (
          <p className="text-sm text-slate-400 p-6 text-center">لا توجد أرقام بعد.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {contacts.map((c) => (
              <div key={c.id} data-testid={`contact-row-${c.id}`} className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 text-sm">{c.name}</div>
                  <div className="text-sm text-slate-500 font-mono-nums" dir="ltr">{c.phone}</div>
                  {c.note ? <div className="text-xs text-slate-400 mt-0.5">{c.note}</div> : null}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 shrink-0">
                    <ContactDialog initial={c} onSaved={load}
                      trigger={<Button data-testid={`edit-contact-${c.id}`} variant="ghost" size="icon" className="h-8 w-8 text-slate-500"><Pencil className="h-4 w-4" /></Button>} />
                    <Button data-testid={`del-contact-${c.id}`} variant="ghost" size="icon" className="h-8 w-8 text-rose-500"
                      onClick={() => setDel({ type: "contact", id: c.id, label: c.name })}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={Boolean(del)} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>تأكيد الحذف</AlertDialogTitle>
            <AlertDialogDescription>هل تريد حذف «{del?.label}»؟</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">حذف</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FaqContactsPanel;
